// The app: who is signed in, the shell around every screen, and which screen
// an address opens.
//
// The shell is a sidebar on a wide screen, and on a phone a top bar and a tab
// bar. A screen with a board takes the phone's tab bar's place for its own
// controls (see BoardScreen.tsx), and its Menu brings the tabs back up.

import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { buildChecked, checkBuild, readJson, sleep, takeEarly } from "./api.ts";
import { refreshDrillBadge } from "./badge.ts";
import { Icon, type IconName } from "./icons.tsx";
import { applyTheme, effectiveTheme } from "./prefs.ts";
import { backLabel, goBack, navigate, replaceWith, setScreenTitle, usePlace } from "./router.ts";
import { onSignedOut, session } from "./session.ts";
import { forgetAll, keep, kept, signOut } from "./kept.ts";
import { TrainScreen } from "../screens/Train.tsx";
import { GameScreen } from "../screens/Game.tsx";
import { AnalyseScreen } from "../screens/Analyse.tsx";
import { DrillScreen } from "../screens/Drill.tsx";
import { TacticsScreen } from "../screens/Tactics.tsx";
import { OpeningsScreen } from "../screens/Openings.tsx";
import { GamesScreen } from "../screens/Games.tsx";
import { SettingsScreen } from "../screens/Settings.tsx";
import { AdminScreen } from "../screens/Admin.tsx";
import { ImportScreen } from "../screens/Import.tsx";
import { LearnScreen } from "../screens/Learn.tsx";
import { ExploreScreen } from "../screens/Explore.tsx";
import { PlayScreen } from "../screens/Play.tsx";
import { LessonScreen } from "../screens/Lesson.tsx";
import { RepertoireScreen } from "../screens/Repertoire.tsx";

// --- THE SCREENS ---

interface NavItem {
  key: string;
  path: string;
  label: string;
  icon: IconName;
  admin?: boolean;
}

export const NAV: NavItem[] = [
  { key: "train", path: "/", label: "Train", icon: "target" },
  { key: "games", path: "/games", label: "Games", icon: "grid" },
  { key: "tactics", path: "/tactics", label: "Tactics", icon: "bars" },
  { key: "openings", path: "/openings", label: "Openings", icon: "book" },
  { key: "analyse", path: "/analyse", label: "Analyse", icon: "search" },
  { key: "settings", path: "/settings", label: "Settings", icon: "sliders" },
  // Only for whoever runs the server, and only in the sidebar: a phone has
  // six tabs and none of them should be a page read once a month.
  { key: "admin", path: "/admin", label: "Admin", icon: "shield", admin: true },
];

/** What the shell needs to know about the screen on show. */
export interface ScreenMeta {
  title: string;
  nav: string;
  /** Where Back goes when nothing of ours is behind the page. */
  back?: string;
  backLabel?: string;
}

export interface ScreenProps {
  params: Record<string, string>;
  query: URLSearchParams;
}

interface Route {
  match: (path: string) => Record<string, string> | null;
  meta: (params: Record<string, string>) => ScreenMeta;
  screen: (props: ScreenProps) => ReactNode;
}

const exact = (want: string) => (path: string) => (path === want ? {} : null);
const pattern = (re: RegExp, ...names: string[]) => (path: string) => {
  const m = path.match(re);
  return m ? Object.fromEntries(names.map((name, i) => [name, m[i + 1]!])) : null;
};

const ROUTES: Route[] = [
  { match: exact("/"), meta: () => ({ title: "Train", nav: "train" }), screen: (p) => <TrainScreen {...p} /> },
  { match: (p) => (p === "/tactics" || p === "/train/motifs" ? {} : null), meta: () => ({ title: "Tactics", nav: "tactics" }), screen: () => <TacticsScreen /> },
  { match: exact("/games"), meta: () => ({ title: "Games", nav: "games" }), screen: () => <GamesScreen /> },
  {
    match: pattern(/^\/games\/([A-Za-z0-9_-]+)\/(white|black)$/, "id", "color"),
    meta: () => ({ title: "Game", nav: "games", back: "/games", backLabel: "Games" }),
    screen: (p) => <GameScreen {...p} />,
  },
  { match: exact("/openings"), meta: () => ({ title: "Openings", nav: "openings" }), screen: () => <OpeningsScreen /> },
  { match: exact("/openings/learn"), meta: () => ({ title: "Learn an opening", nav: "openings", back: "/openings", backLabel: "Openings" }), screen: (p) => <LearnScreen {...p} /> },
  { match: exact("/openings/explore"), meta: () => ({ title: "Explore", nav: "openings", back: "/openings", backLabel: "Openings" }), screen: (p) => <ExploreScreen {...p} /> },
  { match: exact("/openings/import"), meta: () => ({ title: "Import", nav: "openings", back: "/openings", backLabel: "Openings" }), screen: (p) => <ImportScreen {...p} /> },
  {
    match: (p) => (p === "/openings/drill" ? {} : pattern(/^\/openings\/(\d+)\/drill$/, "id")(p)),
    meta: (params) => ({
      title: "Drill",
      nav: "openings",
      back: params.id ? `/openings/${params.id}` : "/openings",
      backLabel: params.id ? "Repertoire" : "Openings",
    }),
    screen: (p) => <DrillScreen {...p} />,
  },
  {
    match: pattern(/^\/openings\/(\d+)\/lesson\/(\d+)$/, "id", "n"),
    meta: (params) => ({ title: "Lesson", nav: "openings", back: `/openings/${params.id}`, backLabel: "Repertoire" }),
    screen: (p) => <LessonScreen {...p} />,
  },
  { match: pattern(/^\/openings\/(\d+)\/play$/, "id"), meta: (params) => ({ title: "Play", nav: "openings", back: `/openings/${params.id}`, backLabel: "Repertoire" }), screen: (p) => <PlayScreen {...p} /> },
  { match: pattern(/^\/openings\/(\d+)$/, "id"), meta: () => ({ title: "Repertoire", nav: "openings", back: "/openings", backLabel: "Openings" }), screen: (p) => <RepertoireScreen {...p} /> },
  { match: exact("/analyse"), meta: () => ({ title: "Analyse", nav: "analyse" }), screen: (p) => <AnalyseScreen {...p} /> },
  { match: exact("/settings"), meta: () => ({ title: "Settings", nav: "settings" }), screen: () => <SettingsScreen /> },
  { match: exact("/admin"), meta: () => ({ title: "Admin", nav: "admin" }), screen: () => <AdminScreen /> },
];

// --- WHAT A SCREEN TELLS THE SHELL ---

interface ShellContext {
  /** A screen with a board says so while it is up: on a phone its controls
   *  take the tab bar's place. */
  setBoardMode(on: boolean): void;
  /** A screen that knows more about where Back goes than its route does. */
  setMeta(meta: ScreenMeta): void;
  openMenu(): void;
}

const Shell = createContext<ShellContext>({ setBoardMode: () => {}, setMeta: () => {}, openMenu: () => {} });
export const useShell = () => useContext(Shell);

/** Back, as a screen draws it on a wide screen: a pill in its top left
 *  corner. On a phone Back is in the top bar. */
export function BackButton({ meta }: { meta: ScreenMeta }) {
  if (!meta.back) return null;
  return (
    <button type="button" className="back" onClick={() => goBack(meta.back!)}>
      <Icon name="back" />
      {backLabel(meta.backLabel || NAV.find((n) => n.path === meta.back)?.label || "Back")}
    </button>
  );
}

// --- THE SHELL ---

function Brand() {
  return (
    <>
      <img className="mark" src="/icon.svg" alt="" />
      <span style={{ color: "inherit" }}>
        Blunder<span>mine</span>
      </span>
    </>
  );
}

function Tabs({ current, onGo }: { current: string; onGo?: () => void }) {
  return (
    <>
      {NAV.filter((item) => !item.admin).map((item) => (
        <button
          key={item.key}
          type="button"
          className="tab"
          data-nav={item.key}
          aria-current={item.key === current ? "page" : "false"}
          onClick={() => {
            onGo?.();
            navigate(item.path);
          }}
        >
          <Icon name={item.icon} />
          {item.label}
          {item.key === "openings" ? <span className="nav-badge" hidden data-badge="openings" /> : null}
        </button>
      ))}
    </>
  );
}

export function App() {
  const place = usePlace();
  const [boardMode, setBoardMode] = useState(false);
  const [menu, setMenu] = useState(false);

  let route: Route | undefined;
  let params: Record<string, string> = {};
  for (const r of ROUTES) {
    const m = r.match(place.path);
    if (m) {
      route = r;
      params = m;
      break;
    }
  }
  const routeMeta = route ? route.meta(params) : { title: "", nav: "" };
  const [override, setOverride] = useState<{ path: string; meta: ScreenMeta } | null>(null);
  const meta = override && override.path === place.path ? override.meta : routeMeta;

  useEffect(() => {
    if (!route) replaceWith("/");
  }, [route]);

  // A new screen starts at the top; one come back to by Back or Forward is
  // scrolled to where it was by the router.
  const lastPath = useRef("");
  useLayoutEffect(() => {
    if (lastPath.current !== place.path && place.how !== "pop") window.scrollTo(0, 0);
    lastPath.current = place.path;
    setMenu(false);
  }, [place.path, place.moved]);

  useEffect(() => {
    setScreenTitle(meta.title);
    document.title = meta.title === "Train" ? "Blundermine" : `${meta.title} · Blundermine`;
    refreshDrillBadge();
  }, [meta.title, place.moved]);

  useEffect(() => {
    document.documentElement.classList.toggle("board-mode", boardMode);
  }, [boardMode]);

  // One object for the life of the app: screens hold on to it in effects.
  const shell = useMemo<ShellContext>(
    () => ({
      setBoardMode,
      setMeta: (m) => setOverride({ path: location.pathname.replace(/\/+$/, "") || "/", meta: m }),
      openMenu: () => setMenu(true),
    }),
    []
  );

  const query = new URLSearchParams(place.search);
  const screen = !route ? null : <route.screen key={place.path} params={params} query={query} />;

  const topTitle = meta.title === "Train" ? "Blundermine" : meta.title;
  return (
    <Shell.Provider value={shell}>
      <div className="app">
        <nav className="nav">
          <a className="brand" href="/" onClick={(e) => (e.preventDefault(), navigate("/"))}>
            <Brand />
          </a>
          {NAV.filter((item) => !item.admin || session.admin).map((item) => (
            <a
              key={item.key}
              className="nav-item"
              href={item.path}
              data-nav={item.key}
              aria-current={item.key === meta.nav ? "page" : "false"}
              onClick={(e) => {
                e.preventDefault();
                navigate(item.path);
              }}
            >
              <Icon name={item.icon} />
              {item.label}
              {item.key === "openings" ? <span className="nav-badge" hidden data-badge="openings" /> : null}
            </a>
          ))}
          <div className="nav-spacer" />
          <div className="nav-user">
            {session.authRequired ? (
              <>
                <div className="email" title={session.email || ""}>
                  {session.email}
                </div>
                <button
                  type="button"
                  className="link-btn"
                  onClick={() => void signOut()}
                >
                  Sign out
                </button>
              </>
            ) : (
              <div className="email">Sign-in off</div>
            )}
          </div>
        </nav>
        <div className="main-col">
          <header className="topbar">
            <div className="slot">
              {meta.back ? (
                <button type="button" aria-label={`Back to ${backLabel(meta.backLabel || "Back")}`} onClick={() => goBack(meta.back!)}>
                  <Icon name="back" />
                </button>
              ) : null}
            </div>
            <h1>{topTitle}</h1>
            <div className="slot" />
          </header>
          <main className="main">{screen}</main>
        </div>
      </div>
      <nav className="tabbar">
        <Tabs current={meta.nav} />
      </nav>
      {menu ? (
        <div className="menu-sheet" role="dialog" aria-label="Menu" onClick={() => setMenu(false)}>
          <nav className="menu-tabs" onClick={(e) => e.stopPropagation()}>
            <Tabs current={meta.nav} onGo={() => setMenu(false)} />
          </nav>
        </div>
      ) : null}
    </Shell.Provider>
  );
}

// --- SIGNING IN, AND STARTING UP ---

interface KeptSession {
  email: string | null;
  admin: boolean;
  authRequired: boolean;
  clientId: string | null;
  analysisVersion: number;
  engine: string;
}

export function Root() {
  // Who was signed in last time, shown at once: the server is asked behind
  // it, and only a session that has since ended stops the app for sign-in.
  const [state, setState] = useState<"loading" | "gate" | "app" | { error: string }>(() => {
    const last = kept<KeptSession>("session");
    if (!last || !last.email) return "loading";
    Object.assign(session, last);
    return "app";
  });
  const [, redraw] = useState(0);

  useEffect(() => {
    onSignedOut(() => setState("gate"));
    applyTheme();
    void boot();
    async function boot() {
      try {
        // Both at once: who may sign in, and who is signed in. Asked one
        // after the other they were two round trips before anything showed.
        const [authRes, me] = await Promise.all([takeEarly("/api/auth") || fetch("/api/auth"), takeEarly("/api/me") || fetch("/api/me")]);
        const cfg = await readJson(authRes);
        session.authRequired = Boolean(cfg.required);
        session.clientId = cfg.client_id || null;
        if (!me.ok) {
          forgetAll();
          if (session.authRequired) return setState("gate");
          throw new Error(`the server answered ${me.status}`);
        }
        const who = await readJson(me);
        // Somebody else signed in on this browser since: what was kept was
        // theirs.
        if (session.email && who.email !== session.email) {
          forgetAll();
          location.reload();
          return;
        }
        session.email = who.email;
        session.admin = Boolean(who.admin);
        session.analysisVersion = who.analysis_version || 0;
        session.engine = who.engine || "";
        session.build = who.build || "";
        keep("session", {
          email: session.email,
          admin: session.admin,
          authRequired: session.authRequired,
          clientId: session.clientId,
          analysisVersion: session.analysisVersion,
          engine: session.engine,
        } satisfies KeptSession);
        buildChecked();
        // A tab can sit open for hours without a navigation or a focus change.
        setInterval(() => void checkBuild(), 5 * 60_000);
        // Links from before the app had screens: /?game=…&color=….
        const q = new URLSearchParams(location.search);
        if (location.pathname === "/" && q.get("game")) {
          const color = q.get("color");
          replaceWith(color ? `/games/${encodeURIComponent(q.get("game")!)}/${color}` : `/analyse?input=${encodeURIComponent(q.get("game")!)}`);
        }
        setState("app");
        redraw((n) => n + 1);
      } catch (err) {
        // Offline, with the app already on the screen: it stays, and says so
        // where it asks the server next.
        setState((was) => (was === "app" ? was : { error: String(err instanceof Error ? err.message : err) }));
      }
    }
  }, []);

  // Until the server has said who is signed in, the page's own placeholder
  // stays as it was drawn: the board, the buttons, nothing blank.
  if (state === "loading") return <div style={{ display: "contents" }} dangerouslySetInnerHTML={{ __html: window.__placeholder || "" }} />;
  if (state === "gate") return <SignIn onIn={() => location.reload()} />;
  if (typeof state === "object") {
    return (
      <div className="gate">
        <div className="card gate-card">
          <p>Cannot reach the server: {state.error}</p>
        </div>
      </div>
    );
  }
  return <App />;
}

declare global {
  interface Window {
    google?: any;
    /** The page's placeholder, as index.html drew it. */
    __placeholder?: string;
  }
}

function SignIn({ onIn }: { onIn: () => void }) {
  const button = useRef<HTMLDivElement>(null);
  const [denied, setDenied] = useState("");

  useEffect(() => {
    const render = () => {
      window.google.accounts.id.initialize({
        client_id: session.clientId,
        callback: (response: { credential?: string }) => void onCredential(response),
        auto_select: true,
        cancel_on_tap_outside: false,
      });
      window.google.accounts.id.renderButton(button.current, {
        theme: effectiveTheme() === "light" ? "outline" : "filled_black",
        size: "large",
        shape: "pill",
        text: "signin_with",
      });
      window.google.accounts.id.prompt();
    };
    async function onCredential(response: { credential?: string }) {
      setDenied("");
      if (!response?.credential) return setDenied("Google did not return a sign-in token. Try again.");
      const send = () =>
        fetch("/api/session", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ credential: response.credential }) });
      try {
        let res = await send();
        // A 5xx here is the proxy or a restart, not a refusal. The token is
        // still good for a moment, so one retry usually lands.
        if (res.status >= 500) {
          await sleep(700);
          res = await send();
        }
        const data = await readJson(res);
        if (!res.ok) throw new Error(data.error || "Sign-in refused.");
        onIn();
      } catch (err) {
        setDenied(String(err instanceof Error ? err.message : err));
      }
    }
    if (window.google?.accounts?.id) return render();
    const script = document.createElement("script");
    // `hl` on the script URL is what actually pins the button's language.
    script.src = "https://accounts.google.com/gsi/client?hl=en";
    script.async = true;
    script.onload = render;
    script.onerror = () => setDenied("Could not load Google sign-in. Check your connection.");
    document.head.appendChild(script);
  }, [onIn]);

  return (
    <div className="gate">
      <div className="card gate-card">
        <div className="brand">
          <Brand />
        </div>
        <p>Your own mistakes, replayed as puzzles.</p>
        <div id="gbutton" ref={button} />
        {denied ? <p className="denied">{denied}</p> : null}
      </div>
    </div>
  );
}
